import type { ReactNode } from "react";
import { Link } from "react-router-dom";

interface EmptyStateProps {
  /** Emoji string or an inline SVG glyph (rendered inside the coaster). */
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    to?: string;
    href?: string;
    onClick?: () => void;
  };
  className?: string;
}

const actionClasses =
  "inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-cta text-white text-sm font-medium hover:bg-cta-hover btn-sheen transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-body";

/**
 * EmptyState — an invitation, not a dead end. The icon sits on a linen
 * "coaster" with the faint offset ring a resting glass leaves beside it.
 */
export function EmptyState({
  icon = "📭",
  title,
  description,
  action,
  className = "",
}: EmptyStateProps) {
  const iconIsText = typeof icon === "string";

  return (
    <div
      className={`flex flex-col items-center justify-center py-16 px-4 text-center ${className}`}
    >
      <span className="relative mb-5 flex h-16 w-16 items-center justify-center">
        <span
          aria-hidden="true"
          className="absolute -right-2.5 -top-2 h-16 w-16 rounded-full border border-copper/15"
        />
        <span
          className="relative flex h-16 w-16 items-center justify-center rounded-full bg-copper-light text-3xl shadow-2xs ring-1 ring-copper/15"
          {...(iconIsText ? { role: "img", "aria-label": title } : { "aria-hidden": true })}
        >
          {icon}
        </span>
      </span>
      <p className="font-display text-lg font-semibold tracking-tight text-text-primary mb-2">
        {title}
      </p>
      {description && (
        <p className="text-sm text-text-muted max-w-xs leading-relaxed">
          {description}
        </p>
      )}
      {action && (
        <div className="mt-6">
          {action.to ? (
            <Link to={action.to} className={actionClasses}>
              {action.label}
            </Link>
          ) : action.href ? (
            <a href={action.href} className={actionClasses}>
              {action.label}
            </a>
          ) : (
            <button onClick={action.onClick} className={actionClasses}>
              {action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
